import { cloneDeep } from 'lodash';
import { batch } from 'react-redux';
import { getItemFeeList } from 'src/action-window/business-logic/action-window-actions';
import {
  convertCartResponse,
  convertLinePromotion,
  splicePromotions,
} from 'src/common/converters/cart-response-journal-converter';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { LineItemStatus } from 'src/common/enumerations/line-item-status';
import { PromotionRewardType } from 'src/common/enumerations/promotion-reward-types';
import { UnitOfMeasureTypes } from 'src/common/enumerations/unit-of-measure-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import {
  LOCAL_CURRENCY_SYMBOL,
  LOCAL_ISO_CURRENCY_CODE,
} from 'src/common/helpers/localized-description-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { TbrActions } from 'src/common/redux/reducers/tbr-reducer';
import { TransactionActions } from 'src/common/redux/reducers/transaction-reducer';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import {
  CartItemViewData,
  CartPaymentData,
  CartResponse,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { exemptTransactionTax } from 'src/tax-exemption/business-logic/tax-exemption';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { convertLineItem } from '../../../common/converters/cart-response-journal-converter';
import { store } from '../../../common/redux/configure-store';
import { ItemListActions } from '../../../common/redux/reducers/item-list-reducer';
import { getCartPromotionMessage } from '../get-promotion-message';
import { msvCheck } from '../msvCheck';

export const applyCartResponse = async (cartResponse: CartResponse): Promise<void> => {
  const span = createSpan('apply-cart-response');

  let tbrReturns: TbrReturns;
  if (store.getState().posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn) {
    tbrReturns = combineReturnItems(cartResponse);
    cartResponse.returns = tbrReturns.combinedItems;
  }

  const currency = cartResponse.cartInfo?.defaultCurrency ?? LOCAL_ISO_CURRENCY_CODE;
  batch((): void => {
    const { fullCartResponse } = getCurrentXLRConfig();

    if (fullCartResponse) {
      if (cartResponse.items) {
        cartResponse.items = updateVoidItems(cartResponse.items);
      }
      if (cartResponse.payments) {
        cartResponse.payments = updateVoidTenders(cartResponse.payments);
      }
      store.dispatch(ItemListActions.removeAllRecords());
    }
    const records = convertCartResponse(cartResponse);

    store.dispatch(ItemListActions.updateItems(cartResponse.items ?? []));
    store.dispatch(ItemListActions.updatePayments(cartResponse.payments ?? []));
    store.dispatch(ItemListActions.updateReturns(cartResponse.returns ?? []));
    store.dispatch(ItemListActions.updateEJ(records, tbrReturns?.selectedLineId));
    store.dispatch(ItemListActions.setTotals(cartResponse.cartInfo?.totals ?? {}, currency));

    const updatedEJItems = store.getState().itemList.ejItems;
    const itemFeeList = getItemFeeList(updatedEJItems);
    store.dispatch(ItemListActions.setItemFeeList(itemFeeList));

    store.dispatch(TransactionActions.setCartStatus(cartResponse.cartInfo?.status));

    if (tbrReturns) {
      store.dispatch(
        TbrActions.updateTbrItemList(tbrReturns.lineQuantityMap, tbrReturns.selectedLineId),
      );
    }
    if (cartResponse.sequenceNumber) {
      store.dispatch(TransactionActions.setTransactionSequenceNumber(cartResponse.sequenceNumber));
    }
  });

  const { taxAuthoritiesInCartList, transactionTaxExemptApplied } = store.getState().transaction;

  if (transactionTaxExemptApplied) {
    const itemsListTaxes = store.getState().itemList.items?.pop() || [];
    const filteredItemTaxes: string[] = Array.from(
      new Set(itemsListTaxes['taxes']?.map(taxItem => taxItem.authorityId)),
    );
    const contains = filteredItemTaxes.every(element =>
      taxAuthoritiesInCartList?.includes(element),
    );
    if (!contains && itemsListTaxes['status'] !== 'Cancelled') {
      await exemptTransactionTax();
    }
  }

  if (
    cartResponse.cartInfo?.status === CartStatus.Total ||
    cartResponse.cartInfo?.status === CartStatus.Itemization
  ) {
    await getCartPromotionMessage(cartResponse);
  }

  const redeemableLoyaltyProgram = cartResponse.rewards?.some(
    reward => reward.redeemableLoyaltyProgram,
  );
  if (
    getCurrentXLRConfig().supportMsvRewards &&
    cartResponse.cartInfo?.status === CartStatus.Total &&
    redeemableLoyaltyProgram
  ) {
    await msvCheck(cartResponse);
  }
  endSpan(span);
};

export const applyTbrItemsResponse = async (cartResponse: CartResponse): Promise<void> => {
  const span = createSpan('apply-TBR-response');
  const tbrLineItem: IElectronicJournalRecord[] = [];
  const itemList =
    cartResponse.items
      ?.filter(item => !item.parentLineId)
      .map(item => convertTbrLineItem(item, cartResponse)) || [];
  const linePromotions: IElectronicJournalRecord[] = [];
  (cartResponse.rewards || []).forEach(reward => {
    // RewardType for Proximity is IncentiveMessageReward' and 'Message' for cashier message
    if (
      reward.rewardType &&
      reward.rewardType !== PromotionRewardType.Message &&
      reward.rewardType !== PromotionRewardType.IncentiveMessageReward
    ) {
      linePromotions.push(convertLinePromotion(reward));
    }
  });
  splicePromotions(linePromotions, itemList);
  const lineTenders = (cartResponse.payments || []).map(convertTbrLineTender);
  const records = tbrLineItem.concat(itemList).concat(lineTenders);
  store.dispatch(TbrActions.setTBRItemsList(records));
  endSpan(span);
};

const convertTbrLineItem = (
  lineItemVal: CartItemViewData,
  cartResponse: CartResponse,
): IElectronicJournalRecord => ({
  ...convertLineItem(lineItemVal, cartResponse, false),
  SequenceNumber: lineItemVal?.lineId,
  Name: lineItemVal?.description || 'item',
  Voided: false,
  Price: convertCurrency(lineItemVal?.extendedAmount ? lineItemVal.extendedAmount : 0),
  Selected: false,
  ItemId: lineItemVal?.lineItem?.itemId?.id,
  EligibilityInfo: [],
  Type: EJRecordTypes.Product,
  UnitOfMeasureCode: lineItemVal?.lineItem?.quantity?.unitOfMeasure || UnitOfMeasureTypes.Each,
  tbrReturnItem: true,
  tbrReturnQty: 0,
  tbrAvailableQty: lineItemVal?.lineItem?.quantity?.value,
  tbrScanData: lineItemVal?.lineItem?.scanData,
});

const convertTbrLineTender = (lineTender: CartPaymentData): IElectronicJournalRecord => {
  const displayAmount =
    lineTender.change && (!lineTender.amount || lineTender.tenderId !== lineTender.tenderChangeId)
      ? lineTender.change
      : lineTender.amount;

  const tenderCurrencySymbol = lineTender.currencySymbol || LOCAL_CURRENCY_SYMBOL;

  const price = `${tenderCurrencySymbol}${(displayAmount || 0).toFixed(2)}`;

  const ejRecord: IElectronicJournalRecord = {
    Type: EJRecordTypes.Tender,
    SequenceNumber: lineTender.sequenceNumber ?? 0,
    Name: lineTender.tenderName ?? '',
    Price: price,
    Voided: false,
    Selected: false,
    EligibilityInfo: [],
    tbrReturnItem: false,
    TenderType: lineTender.tenderType,
    Sequence: lineTender.sequence ?? '0',
  };

  if (lineTender.isProcessedByEps) {
    ejRecord.IsProcessedByEps = true;
    ejRecord.EpsData = {
      authCode: lineTender.epsData?.authCode,
      referenceId: lineTender.epsData?.referenceId,
      amountDue: lineTender.amount,
      tenderId: lineTender.tenderId,
    };
  }

  return ejRecord;
};
// This function only process SE return properly
// R10 may need refactor based on RTI response

export const combineReturnItems = (cartResponse: CartResponse): TbrReturns => {
  const lineQuantityMap = new Map<number, number>();
  const newReturns: CartItemViewData[] = [];
  let lastUpdatedItem: CartItemViewData = { lineId: 0 };

  cartResponse.returns?.forEach(lineItem => {
    const item = cloneDeep(lineItem);
    if (item.lineId > lastUpdatedItem.lineId && !item.parentLineId) {
      lastUpdatedItem = {
        lineId: item.lineId,
        originalPurchasedLineId: item.originalPurchasedLineId,
      };
    }
    if (item.originalPurchasedLineId) {
      const combinedQuantity = lineQuantityMap.get(item.originalPurchasedLineId);

      if (combinedQuantity !== undefined) {
        lineQuantityMap.set(
          item.originalPurchasedLineId,
          combinedQuantity + (item.lineItem?.quantity?.value || 0),
        );
        const updateItem = newReturns[item.originalPurchasedLineId];
        if (updateItem.lineItem?.quantity?.value) {
          updateItem.lineItem.quantity.value += item.lineItem?.quantity?.value || 0;
        }
        if (updateItem.extendedAmount) {
          updateItem.extendedAmount += item.extendedAmount || 0;
        }
        if (updateItem.amount) {
          updateItem.amount += item.amount || 0;
        }
      } else {
        lineQuantityMap.set(item.originalPurchasedLineId, item.lineItem?.quantity?.value || 0);
        item.lineId = item.originalPurchasedLineId;
        if (item.parentLineId) {
          const linkedItem = cartResponse.returns?.find(
            parentItem => item.parentLineId === parentItem.lineId,
          );
          item.parentLineId = linkedItem ? linkedItem.originalPurchasedLineId : item.parentLineId;
        }
        newReturns[item.originalPurchasedLineId] = item;
      }
    }
  });
  const combinedItems = newReturns.filter(item => item);

  return {
    combinedItems,
    lineQuantityMap,
    selectedLineId: lastUpdatedItem.originalPurchasedLineId ?? 0,
  };
};

interface TbrReturns {
  combinedItems: CartItemViewData[];
  lineQuantityMap: Map<number, number>;
  selectedLineId: number;
}

const updateVoidItems = (items: CartItemViewData[]): CartItemViewData[] => {
  const itemList = store.getState().itemList.items || [];
  const existingVoidItems = itemList.filter(item => item.status === LineItemStatus.Cancelled);
  const returnedVoidItems = items.filter(item => item.status === LineItemStatus.Cancelled);
  const nonVoidItems = items.filter(item => item.status !== LineItemStatus.Cancelled);
  const newVoidItems: CartItemViewData[] = [];

  returnedVoidItems.forEach(voidedItem => {
    const itemToVoid = itemList.find(item => item.lineId === voidedItem.lineId);
    if (itemToVoid) {
      if (itemToVoid.status !== LineItemStatus.Cancelled) {
        itemToVoid.status = LineItemStatus.Cancelled;
        newVoidItems.push(itemToVoid);
      }
    } else {
      newVoidItems.push(voidedItem);
    }
  });
  return nonVoidItems.concat(existingVoidItems).concat(newVoidItems);
};

const updateVoidTenders = (tenders: CartPaymentData[]): CartPaymentData[] => {
  const itemList = store.getState().itemList.payments || [];
  const existingVoidTenders = itemList.filter(tender => tender.status === LineItemStatus.Cancelled);
  const returnedVoidTenders = tenders.filter(tender => tender.status === LineItemStatus.Cancelled);
  const nonVoidTender = tenders.filter(tender => tender.status !== LineItemStatus.Cancelled);
  const newVoidTender: CartPaymentData[] = [];

  returnedVoidTenders.forEach(voidedTender => {
    const tenderToVoid = itemList.find(item => item.lineId === voidedTender.lineId);
    if (tenderToVoid) {
      if (tenderToVoid.status !== LineItemStatus.Cancelled) {
        tenderToVoid.status = LineItemStatus.Cancelled;
        newVoidTender.push(tenderToVoid);
      }
    } else {
      newVoidTender.push(voidedTender);
    }
  });
  return nonVoidTender.concat(existingVoidTenders).concat(newVoidTender);
};
